import { Maybe } from "graphql/jsutils/Maybe";
import { REGIONS, TRegion, TRegionKey } from "../models/region-model";

const SMOKEBALL_REGION_STORAGE_KEY = "SmokeballRegion";

export const getCurrentTimeZone = () => {
  const timeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return timeZone;
};

export const getSmokeballRegionFromCurrentTimeZone = () => {
  const timeZone = getCurrentTimeZone().toLowerCase();

  if (!timeZone) {
    return null;
  }
  return regionFromNameContains(timeZone);
};

/*
 * Because these services can be used in taskpane or dialogs sometimes we want
 * to use session storage and other times local storage.
 *
 * Dialogs should use session storage.
 *
 * Loading in any env should prefer session.
 *
 * If the region is cleared it will clear both session and local storages.
 *
 */

export const storeRegionInBrowser = (regionKey: TRegionKey | null, useSession?: boolean) => {
  if (regionKey) {
    const storage = useSession ? sessionStorage : localStorage;
    storage.setItem(SMOKEBALL_REGION_STORAGE_KEY, regionKey);
  } else {
    localStorage.removeItem(SMOKEBALL_REGION_STORAGE_KEY);
    sessionStorage.removeItem(SMOKEBALL_REGION_STORAGE_KEY);
  }
};

export const loadRegionFromBrowser = (): TRegionKey | null => {
  const loaded =
    sessionStorage.getItem(SMOKEBALL_REGION_STORAGE_KEY) || localStorage.getItem(SMOKEBALL_REGION_STORAGE_KEY);
  return regionFromCode(loaded)?.code || null;
};

export const regionFromCode = (code: Maybe<string>) => {
  if (!code || typeof code !== "string") {
    return null;
  }

  const found = Object.values(REGIONS).find((r) => r.code === code.toLocaleUpperCase());

  return found || null;
};

export const regionFromName = (name: Maybe<string>) => {
  if (!name || typeof name !== "string") {
    return null;
  }

  const found = Object.values(REGIONS).find((r) => r.name.toLocaleLowerCase() === name.toLocaleLowerCase());

  return found || null;
};

// looks like this is a fallback to provide a default region
export const regionFromNameContains = (slug: Maybe<string>) => {
  if (!slug || typeof slug !== "string") {
    return REGIONS.AUS;
  }

  const found = Object.values(REGIONS).find((r) => r.name.toLocaleLowerCase().includes(slug.toLocaleLowerCase()));

  return found || REGIONS.AUS;
};

export const bestGuessRegion = () => {
  let region: Maybe<TRegion>;

  region = regionFromCode(loadRegionFromBrowser());
  if (region) {
    return region;
  }

  region = getSmokeballRegionFromCurrentTimeZone();
  if (region) {
    return region;
  }

  region = REGIONS.USA;

  return region;
};
